// client/src/components/OfflineQueueBanner.tsx
//
// Barra di stato della connessione con contatore delle azioni in attesa.
//
// Prima esisteva solo un avviso testuale "Nessuna connessione Internet": non
// diceva all'utente se il lavoro appena fatto era al sicuro né gli permetteva
// di forzare l'invio. Ora la barra unisce le due informazioni e offre il
// retry manuale quando la rete è tornata ma la coda non si è ancora svuotata.

import React from 'react';
import { useOfflineSync } from '@/hooks/infrastructure/useOfflineSync';

interface OfflineQueueBannerProps {
  /** Stato di rete rilevato da NetworkProvider (Capacitor-aware). */
  isOnline: boolean;
}

export default function OfflineQueueBanner({ isOnline }: OfflineQueueBannerProps) {
  const { pendingCount, isSyncing, sync } = useOfflineSync({ onlineOverride: isOnline });
  const hasPending = pendingCount > 0;

  if (isOnline && !hasPending) return null;

  const plural = pendingCount === 1 ? 'azione' : 'azioni';

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-0 left-0 right-0 z-50 text-center text-sm font-semibold py-2 px-4 text-white ${
        isOnline ? 'bg-amber-600' : 'bg-red-600'
      }`}
    >
      {!isOnline ? (
        <>
          📴 Nessuna connessione Internet
          {hasPending
            ? ` — ${pendingCount} ${plural} in attesa di invio`
            : ' — alcuni dati potrebbero non essere aggiornati'}
        </>
      ) : (
        <span className="inline-flex items-center gap-3">
          🔄 {pendingCount} {plural} in attesa di invio
          <button
            type="button"
            onClick={() => {
              void sync();
            }}
            disabled={isSyncing}
            className="rounded bg-white/20 px-2 py-0.5 text-xs font-bold uppercase tracking-wide disabled:opacity-60"
          >
            {isSyncing ? 'Invio…' : 'Riprova ora'}
          </button>
        </span>
      )}
    </div>
  );
}
